import { X, Check, Loader2 } from "lucide-react"
import { Microphone } from 'iconoir-react';
import { useTranslation } from 'react-i18next';
import { useTheme } from "components/core/theme-provider"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'components/ui/tooltip';
import useAudioRecorder from "../hooks/use-audio-recorder";
import { useSpeechToText } from "../hooks/use-speech-to-text";
import { TranscribeAudioToTextResponse } from "../types/model.types";
import { useEffect, useRef } from "react";

interface DictateButtonProps {
    onTranscribe: (content: string) => void;
    setIsDictationOnGoing: (isDictationOnGoing: boolean) => void;
    isDictationOnGoing: boolean;
    disabled?: boolean;
    /** Changing it (e.g. switching conversation) cancels an in-progress dictation. */
    resetKey?: string;
}

interface DictateMicButtonProps {
    disabled: boolean;
    isPressed: boolean;
    isRecording?: boolean;
    onClick?: () => void;
    className?: string;
}

const DictateMicButton = ({
    disabled,
    isPressed,
    isRecording = false,
    onClick,
    className = "",
}: DictateMicButtonProps) => {
    const { t } = useTranslation();
    const { theme } = useTheme();
    const iconColor = theme === 'dark' ? 'text-true-white' : 'text-grey-900';
    const label = t('PRESS_HOLD', { defaultValue: 'Press and hold' });

    return (
    <TooltipProvider delayDuration={0}>
        <Tooltip>
            <TooltipTrigger asChild>
                <button
                    type="button"
                    aria-pressed={isPressed}
                    aria-label="Dictate button"
                    className={`flex h-7 w-7 min-w-7 shrink-0 items-center justify-center p-0 hover:rounded-full hover:bg-muted disabled:opacity-40 2xl:h-8 2xl:min-w-8 2xl:w-8 ${isRecording ? 'animate-pulse-glow' : ''} ${className}`}
                    onClick={onClick}
                    disabled={disabled}
                >
                    <Microphone
                        width={20}
                        height={20}
                        strokeWidth={1.5}
                        className={`size-4 shrink-0 2xl:size-5 ${iconColor}`}
                    />
                </button>
            </TooltipTrigger>
            <TooltipContent side="top">{label}</TooltipContent>
        </Tooltip>
    </TooltipProvider>
    );
};

export const DictateButton = ({
    onTranscribe,
    setIsDictationOnGoing,
    isDictationOnGoing,
    disabled = false,
    resetKey,
}: DictateButtonProps) => {
    const { theme } = useTheme();
    const { isRecording, startRecording, stopRecording, cancelRecording, canvasRef } = useAudioRecorder();
    const { mutateAsync: transcribeAudioToText, isPending: isTranscribing } = useSpeechToText();

    const previousResetKeyRef = useRef(resetKey);
    useEffect(() => {
        if (previousResetKeyRef.current === resetKey) return;
        previousResetKeyRef.current = resetKey;
        cancelRecording();
        setIsDictationOnGoing(false);
        // Only a chat change should cancel; the callbacks are recreated every render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resetKey]);

    // Match other composer controls: stop STT UI while a reply is streaming.
    useEffect(() => {
        if (!disabled || !isDictationOnGoing) return;
        cancelRecording();
        setIsDictationOnGoing(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [disabled, isDictationOnGoing]);

    const handleStartRecording = async () => {
        if (disabled) return;
        try {
            await startRecording();
            setIsDictationOnGoing(true);
        } catch (error) {
            console.log(error);
            setIsDictationOnGoing(false);
        }
    }

    const handleStopRecording = async () => {
        if (!isRecording) return;

        try {
            const audioBlob = await stopRecording() as Blob | null;
            if (audioBlob) {
                const res = await transcribeAudioToText(audioBlob) as TranscribeAudioToTextResponse;
                console.log(res);
                setIsDictationOnGoing(false);
                if (res) {
                    onTranscribe?.(res.text);
                } else {
                    console.warn('Transcription response missing text or has error:', res);
                }
            }
        } catch (error) {
            console.error('Error in handleStopRecording:', error);
            setIsDictationOnGoing(false);
        }
    }

    const handleCancelRecording = () => {
        setIsDictationOnGoing(false);
        cancelRecording();
    }

    if (isDictationOnGoing) {
        return (
            <div className="flex w-full min-w-0 items-center gap-1 pl-0">
                <DictateMicButton
                    disabled={isTranscribing || disabled}
                    isPressed={true}
                    className={`${theme === 'dark' ? 'border-black-200 opacity-70' : 'border-black-300 opacity-70'} cursor-default`}
                />
                <div className="min-w-0 flex-2">
                    <canvas ref={canvasRef} height={32} className="w-full rounded" />
                </div>

                <button
                    aria-pressed="true"
                    aria-label="Cancel recording button"
                    className={
                        `flex h-9 min-w-9 items-center justify-center sm:p-2 p-1 rounded-full border-[0.5px] border-black-500 shadow-sm hover:bg-muted disabled:opacity-40`
                    }
                    disabled={isTranscribing || disabled}
                    onClick={handleCancelRecording}
                >
                    <X size={16} className={`${theme === 'dark' ? 'text-true-white' : 'text-grey-900'}`} />
                </button>

                {!isTranscribing && <button
                    aria-pressed="true"
                    aria-label="Stop recording button"
                    className={
                        'flex h-9 min-w-9 items-center justify-center sm:p-2 p-1 rounded-full border-[0.5px] border-black-500 shadow-sm hover:bg-muted disabled:opacity-30 '
                        // + (theme === 'dark' ? 'border-gray-500 opacity-70' : 'border-gray-200')
                    }
                    disabled={disabled}
                    onClick={handleStopRecording}
                >
                    <Check size={16} className={`${theme === 'dark' ? 'text-true-white' : 'text-grey-900'}`} />
                </button>}
                {isTranscribing && <button
                    aria-pressed="true"
                    aria-label="Transcribing loader button"
                    className={
                        `flex h-9 min-w-9 items-center justify-center sm:p-2 p-1 rounded-full border-[0.5px] border-black-500 shadow-sm hover:bg-muted disabled:opacity-30`
                    }
                >
                    <Loader2 className={`animate-spin ${theme === 'dark' ? 'text-true-white' : 'text-grey-900'}`} />
                </button>}
            </div>
        )
    }

    return (
        <DictateMicButton
            isPressed={false}
            onClick={handleStartRecording}
            disabled={isTranscribing || disabled}
            isRecording={isRecording}
            className={`${theme === 'dark' ? 'border-black-500' : 'border-black-100'} ${isRecording ? 'bg-primary-50 border-transparent' : ''}`}
        />
    )
}
